import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useEffect, useCallback } from 'react'
import { FullPage } from '@/components/common/FullPage'
import { Button } from '@/components/common/Button'
import { usePublishedChangelog, useMarkAllChangelogRead } from '@/hooks/useChangelog'
import { useAnalytics } from '@/hooks/useAnalytics'
import { AnalyticsEvents } from '@/lib/analytics-events'
import changelogStyles from '@/components/features/changelog/WhatsNewSheet.module.css'

export const Route = createFileRoute('/_authenticated/whats-new')({
  component: WhatsNewPage,
})

/**
 * Simple markdown renderer for admin-authored content.
 * Converts basic markdown syntax to HTML.
 */
function renderMarkdown(content: string): string {
  let html = content
    // Escape HTML entities first
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  // Headings
  html = html.replace(/^### (.+)$/gm, '<h4>$1</h4>')
  html = html.replace(/^## (.+)$/gm, '<h3>$1</h3>')

  // Bold and italic
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/_(.+?)_/g, '<em>$1</em>')

  // Images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="changelog-image" />')

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')

  // Bullet lists
  html = html.replace(/(^- .+$(\n|$))+/gm, (match) => {
    const items = match.trim().split('\n').map(line =>
      `<li>${line.replace(/^- /, '')}</li>`
    ).join('')
    return `<ul>${items}</ul>`
  })

  // Line breaks (but not inside block elements)
  html = html.replace(/\n/g, '<br />')
  html = html.replace(/<\/(h[34]|ul|li)><br \/>/g, '</$1>')
  html = html.replace(/<br \/><(h[34]|ul)/g, '<$1')

  return html
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function WhatsNewPage() {
  const router = useRouter()
  const handleBack = useCallback(() => {
    router.history.back()
  }, [router])
  const { data: entries = [], isLoading } = usePublishedChangelog()
  const markAllRead = useMarkAllChangelogRead()
  const { capture } = useAnalytics()

  // Mark all as read and track analytics when page mounts
  useEffect(() => {
    capture(AnalyticsEvents.CHANGELOG_VIEWED)
    markAllRead.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const headerAction = entries.length > 0 ? (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => markAllRead.mutate()}
    >
      Mark All Read
    </Button>
  ) : undefined

  return (
    <FullPage
      title="What's New"
      onBack={handleBack}
      actions={headerAction}
    >
      <div className={changelogStyles.content}>
        {isLoading && (
          <div className={changelogStyles.loading}>Loading...</div>
        )}

        {!isLoading && entries.length === 0 && (
          <div className={changelogStyles.empty}>
            <p className={changelogStyles.emptyText}>No updates yet. Check back soon!</p>
          </div>
        )}

        {entries.map((entry) => (
          <article key={entry.id} className={changelogStyles.entry}>
            <div className={changelogStyles.entryHeader}>
              <h3 className={changelogStyles.entryTitle}>{entry.title}</h3>
              <div className={changelogStyles.entryMeta}>
                {entry.published_at && (
                  <time className={changelogStyles.entryDate} dateTime={entry.published_at}>
                    {formatDate(entry.published_at)}
                  </time>
                )}
                {entry.version && (
                  <span className={changelogStyles.entryVersion}>v{entry.version.replace(/^v/i, '')}</span>
                )}
              </div>
            </div>
            <div
              className={changelogStyles.entryContent}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(entry.content) }}
            />
          </article>
        ))}
      </div>
    </FullPage>
  )
}
